import { ReactNode, useRef } from "react";
import { useDocumentDrag } from "@/contexts/DocumentDragContext";
import { cn } from "@/lib/utils";

interface DraggableDocumentProps {
  documentId: string;
  documentTitle: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

export function DraggableDocument({
  documentId,
  documentTitle,
  children,
  className,
  disabled = false,
}: DraggableDocumentProps) {
  const { startDrag, endDrag, isDragging } = useDocumentDrag();
  const elementRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (e: React.DragEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    
    // Set drag data
    e.dataTransfer.setData("text/plain", documentId);
    e.dataTransfer.effectAllowed = "move";
    
    // Start drag in context
    startDrag({ id: documentId, title: documentTitle });
    
    // Add a subtle drag image effect
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      e.dataTransfer.setDragImage(elementRef.current, rect.width / 2, 20);
    }
  };

  const handleDragEnd = () => {
    endDrag();
  };

  return (
    <div
      ref={elementRef}
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(
        "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50",
        className
      )}
    >
      {children}
    </div>
  );
}
